/**
 * SISA client tulis-tangan era Hono — HANYA untuk permukaan yang BELUM publik di kontrak.
 *
 * Seluruh isi berkas ini dulunya menutupi 14 kelompok endpoint. Tiga belas di antaranya
 * sudah digantikan hooks generated orval (lihat ./api) karena operasinya memang
 * `security: []` di kontrak. Yang tersisa di bawah adalah empat kelompok yang TIDAK bisa
 * ikut migrasi hari ini, dan alasannya sama untuk keempatnya:
 *
 *   /pub, /pub/by-slug/{slug}, /pub/categories, /project, /project/{id},
 *   /project/by-slug/{slug}, /project/reports*, /project/report-categories,
 *   /project/report-tags
 *
 * SUDAH DIPUTUSKAN (2026-08-26) — bentuk (a) yang mendarat. Kedua belas operasi di atas kini
 * `security: []` dan PUNYA hook generated di `./api`. Kekhawatiran yang menahan keputusan itu
 * dijawab di server, bukan di sini: `status` memang query param bebas yang hanya difilter
 * kalau dikirim, jadi gerbangnya sekarang PREDIKAT kueri yang menyala saat
 * `!ctx.IsAuthenticated()` — `anonPublishedOnly` di feature/pub, dan trio
 * `anonProjectPublishedOnly`/`anonReportPublishedOnly`/`anonPublicationPublishedOnly` di
 * feature/project (kolomnya beda per entitas; alasannya di kepala helper itu). Panel admin
 * memakai operationId yang sama dan TIDAK terpengaruh: bersesi berarti predikatnya no-op.
 * Bentuk (b) — operasi `/public/...` tersendiri — sengaja TIDAK dipakai.
 *
 * Yang membuat berkas ini masih ada: pemanggilnya belum dipindah. Keempat objek di bawah
 * sekarang DUPLIKAT dari hooks generated dan dijadwalkan dihapus begitu hook milik aurora,
 * breeze, verdant dan zenith pindah ke `./api`. Jangan tambah metode baru di sini.
 *
 * Satu kasus TIDAK punya padanan sama sekali dan tidak akan dapat hook lewat jalur mana pun:
 * `GET /pub/categories/slug/{slug}` tidak ada di kontrak DAN tidak ada di gobackend — ia
 * peninggalan backend Hono. `getCategoryBySlug` di bawah karena itu disaring dari
 * `getCategories` di klien, bukan memanggil endpoint yang tidak ada.
 */
import { API_BASE_URL, customFetch } from './legacy-fetch'
import type { ApiResponse, Category, Project, ProjectReport, Publication, Tag } from './types'

/*
 * BENTUK AMPLOP — kenapa `result.data`, bukan `result.data.data`.
 *
 * Metode di bawah dulu membaca `result.data?.data` dan `result.data?.meta`. Itu bentuk amplop
 * HONO, dan ia sudah tidak berlaku: gobackend memakai amplop v2, yang menaruh array LANGSUNG
 * di `data` dan paginasinya di `metaData.pagination` (httpx.Pagination — page/limit/total/
 * totalPages), bukan di `data.meta`.
 *
 * Akibatnya `result.data?.data` selalu `undefined` dan tiap metode memulangkan `[]`. Bug ini
 * TIDAK terlihat selama endpointnya masih 401: fetch gagal lebih dulu, jadi kegagalan
 * unwrap-nya tidak pernah tercapai. Begitu 401 diperbaiki, permintaannya 200 berisi data —
 * dan halaman TETAP kosong. Dua cacat berlapis di jalur yang sama, yang kedua bersembunyi di
 * balik yang pertama.
 *
 * Fallback `|| []` yang membuatnya senyap: tanpa itu, unwrap yang salah akan melempar dan
 * ketahuan sejak hari pertama.
 *
 * Yang membuktikan bentuk ini benar, bukan tebakan: `use-publications-load-more.ts:40` sudah
 * memakai `result.data || []` dan halaman itu memang jalan.
 */

export const publicationApi = {
    async getPublications(params: {
        type?: 'NEWS' | 'PAGE' | 'REPORT';
        categoryId?: string;
        search?: string;
        page?: string | number;
        limit?: string | number;
        status?: 'PUBLISHED';
    }): Promise<ApiResponse<Publication[]>> {
        const searchParams = new URLSearchParams()

        // Only add published content for public website
        searchParams.set('status', 'PUBLISHED')

        if (params.type) searchParams.set('type', params.type)
        if (params.categoryId) searchParams.set('categoryId', params.categoryId)
        if (params.search) searchParams.set('search', params.search)
        if (params.page) {
            const pageNum = typeof params.page === 'string' ? parseInt(params.page) : params.page
            if (!isNaN(pageNum) && pageNum > 0) {
                searchParams.set('page', pageNum.toString())
            }
        }
        if (params.limit) {
            const limitNum = typeof params.limit === 'string' ? parseInt(params.limit) : params.limit
            if (!isNaN(limitNum) && limitNum > 0) {
                searchParams.set('limit', limitNum.toString())
            }
        }

        const response = await customFetch(`${API_BASE_URL}/pub?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data || [],
            status: 'success'
        }
    },

    async getPublicationBySlug(slug: string): Promise<ApiResponse<Publication>> {
        const response = await customFetch(`${API_BASE_URL}/pub/by-slug/${slug}`)
        return response.json()
    },

    async getRecentPublications(params: {
        type?: 'NEWS' | 'PAGE' | 'REPORT';
        limit?: number;
    } = {}): Promise<ApiResponse<Publication[]>> {
        const searchParams = new URLSearchParams()
        searchParams.set('status', 'PUBLISHED')
        searchParams.set('sortBy', 'publishedAt')
        searchParams.set('sortOrder', 'desc')

        if (params.type) searchParams.set('type', params.type)
        if (params.limit) searchParams.set('limit', params.limit.toString())

        const response = await customFetch(`${API_BASE_URL}/pub?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data || [],
            status: 'success'
        }
    },

    async getPopularPublications(params: {
        type?: 'NEWS' | 'PAGE' | 'REPORT';
        limit?: number;
    } = {}): Promise<ApiResponse<Publication[]>> {
        const searchParams = new URLSearchParams()
        searchParams.set('status', 'PUBLISHED')
        searchParams.set('sortBy', 'createdAt')
        searchParams.set('sortOrder', 'desc')

        if (params.type) searchParams.set('type', params.type)
        if (params.limit) searchParams.set('limit', params.limit.toString())

        const response = await customFetch(`${API_BASE_URL}/pub?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data || [],
            status: 'success'
        }
    },

    async getFeaturedPublications(params: {
        type?: 'NEWS' | 'PAGE' | 'REPORT';
        limit?: number;
    } = {}): Promise<ApiResponse<Publication[]>> {
        const searchParams = new URLSearchParams()
        searchParams.set('status', 'PUBLISHED')
        searchParams.set('isFeatured', 'true')
        searchParams.set('sortBy', 'publishedAt')
        searchParams.set('sortOrder', 'desc')

        if (params.type) searchParams.set('type', params.type)
        if (params.limit) searchParams.set('limit', params.limit.toString())

        const response = await customFetch(`${API_BASE_URL}/pub?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data || [],
            status: 'success'
        }
    }
}

// Category API
export const categoryApi = {
    async getCategories(): Promise<ApiResponse<Category[]>> {
        const response = await customFetch(`${API_BASE_URL}/pub/categories`)
        return response.json()
    },

    // `GET /pub/categories/slug/{slug}` TIDAK ADA — tidak di kontrak, tidak di gobackend.
    // Ia peninggalan backend Hono, dan pemanggilan lama ke sana sudah 404 sejak gobackend
    // menggantikannya. Disaring dari daftar kategori di klien: daftarnya kecil (satu
    // organisasi) dan sudah diambil halaman yang sama, jadi ini bukan pengambilan tambahan
    // yang mahal — dan yang lebih penting, ia BEKERJA, sedangkan versi sebelumnya tidak.
    async getCategoryBySlug(slug: string): Promise<ApiResponse<Category>> {
        const semua = await this.getCategories()
        const cocok = (semua.data ?? []).find((c) => c.slug === slug)
        if (!cocok) {
            throw new Error(`Kategori dengan slug "${slug}" tidak ditemukan`)
        }
        return { data: cocok, status: 'success' }
    }
}

// Project API
export const projectApi = {
    async getProjects(params: {
        search?: string;
        page?: string | number;
        limit?: string | number;
        status?: 'COMPLETED' | 'IN_PROGRESS' | 'PLANNING' | 'ON_HOLD';
        sortBy?: 'createdAt' | 'startDate' | 'endDate' | 'title';
        sortOrder?: 'asc' | 'desc';
    } = {}): Promise<ApiResponse<Project[]>> {
        const searchParams = new URLSearchParams()

        if (params.search) searchParams.set('search', params.search)

        // Provide default values for required fields
        const pageNum = params.page ?
            (typeof params.page === 'string' ? parseInt(params.page) : params.page) : 1
        const limitNum = params.limit ?
            (typeof params.limit === 'string' ? parseInt(params.limit) : params.limit) : 10

        // Ensure valid numbers
        if (!isNaN(pageNum) && pageNum > 0) {
            searchParams.set('page', pageNum.toString())
        } else {
            searchParams.set('page', '1')
        }

        if (!isNaN(limitNum) && limitNum > 0) {
            searchParams.set('limit', limitNum.toString())
        } else {
            searchParams.set('limit', '10')
        }

        if (params.status) searchParams.set('status', params.status)
        if (params.sortBy) searchParams.set('sortBy', params.sortBy)
        if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder)

        const response = await customFetch(`${API_BASE_URL}/project?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data || [],
            status: 'success',
            // @ts-ignore - Extended response untuk projects dengan pagination
            meta: result.metaData?.pagination || null
        }
    },

    async getProjectById(id: string): Promise<ApiResponse<Project>> {
        const response = await customFetch(`${API_BASE_URL}/project/${id}`)
        return response.json()
    },

    async getProjectBySlug(slug: string): Promise<ApiResponse<Project>> {
        const response = await customFetch(`${API_BASE_URL}/project/by-slug/${slug}`)
        return response.json()
    }
}

// Project Reports API - Using dedicated project report endpoints
export const projectReportApi = {
    async getReports(params: {
        projectId?: string;
        status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
        type?: 'PROGRESS' | 'MILESTONE' | 'FINAL' | 'ISSUE';
        search?: string;
        page?: string | number;
        limit?: string | number;
        sortBy?: 'createdAt' | 'publishedAt' | 'reportDate' | 'title';
        sortOrder?: 'asc' | 'desc';
    } = {}): Promise<ApiResponse<ProjectReport[]>> {
        const searchParams = new URLSearchParams()

        // For public website, only show published reports
        searchParams.set('status', 'PUBLISHED')

        if (params.projectId) searchParams.set('projectId', params.projectId)
        if (params.type) searchParams.set('type', params.type)
        if (params.search) searchParams.set('search', params.search)

        // Handle pagination
        const pageNum = params.page ?
            (typeof params.page === 'string' ? parseInt(params.page) : params.page) : 1
        const limitNum = params.limit ?
            (typeof params.limit === 'string' ? parseInt(params.limit) : params.limit) : 10

        if (!isNaN(pageNum) && pageNum > 0) {
            searchParams.set('page', pageNum.toString())
        }
        if (!isNaN(limitNum) && limitNum > 0) {
            searchParams.set('limit', limitNum.toString())
        }

        // Convert page to offset for backend
        const offset = (pageNum - 1) * limitNum
        searchParams.set('offset', offset.toString())

        // Handle sorting
        if (params.sortBy) searchParams.set('sortBy', params.sortBy)
        if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder)

        const response = await customFetch(`${API_BASE_URL}/project/reports?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data || [],
            status: 'success',
            // @ts-ignore - Extended response untuk reports dengan meta
            meta: result.metaData?.pagination || null
        }
    },

    async getReportBySlug(slug: string): Promise<ApiResponse<ProjectReport>> {
        const response = await customFetch(`${API_BASE_URL}/project/reports/by-slug/${slug}`)
        return response.json()
    },

    async getReportsByProject(projectId: string, params: {
        page?: string | number;
        limit?: string | number;
    } = {}): Promise<ApiResponse<ProjectReport[]>> {
        const searchParams = new URLSearchParams()
        searchParams.set('status', 'PUBLISHED')

        const pageNum = params.page ?
            (typeof params.page === 'string' ? parseInt(params.page) : params.page) : 1
        const limitNum = params.limit ?
            (typeof params.limit === 'string' ? parseInt(params.limit) : params.limit) : 10

        const offset = (pageNum - 1) * limitNum
        searchParams.set('offset', offset.toString())
        searchParams.set('limit', limitNum.toString())
        searchParams.set('sortBy', 'reportDate')
        searchParams.set('sortOrder', 'desc')

        const response = await customFetch(`${API_BASE_URL}/project/reports/by-project/${projectId}`)
        const result = await response.json()
        return {
            data: result.data || [],
            status: 'success'
        }
    },

    async getRecentReports(params: {
        limit?: number;
        type?: 'PROGRESS' | 'MILESTONE' | 'FINAL' | 'ISSUE';
    } = {}): Promise<ApiResponse<ProjectReport[]>> {
        const searchParams = new URLSearchParams()
        searchParams.set('status', 'PUBLISHED')
        searchParams.set('sortBy', 'publishedAt')
        searchParams.set('sortOrder', 'desc')
        searchParams.set('offset', '0')

        if (params.limit) searchParams.set('limit', params.limit.toString())
        if (params.type) searchParams.set('type', params.type)

        const response = await customFetch(`${API_BASE_URL}/project/reports?${searchParams}`)
        const result = await response.json()
        return {
            data: result.data || [],
            status: 'success'
        }
    },

    async getReportStats(): Promise<ApiResponse<{
        progress: number;
        milestone: number;
        final: number;
        issue: number;
        total: number;
    }>> {
        const response = await customFetch(`${API_BASE_URL}/project/reports/stats`)
        return response.json()
    },

    async getReportCategories(): Promise<ApiResponse<Category[]>> {
        const response = await customFetch(`${API_BASE_URL}/project/report-categories`)
        return response.json()
    },

    async getReportTags(): Promise<ApiResponse<Tag[]>> {
        const response = await customFetch(`${API_BASE_URL}/project/report-tags`)
        return response.json()
    }
}
